import React, { useEffect, useRef, useState } from 'react';
import { getReportDiagnostics, submitProblemReport } from '../../utils/submitProblemReport';
import './ReportProblem.scss';

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

// Self-contained overlay + form. Deliberately NOT redux-connected: the app's
// ErrorBoundary renders it after the React tree has crashed (no Provider),
// so everything it needs arrives via props.
//   feature      — what the user was using (activeTab), for triage
//   source       — entry point: user-menu | help-menu | error-screen
//   errorContext — error details when opened from an error screen
//   onClose      — dismiss callback
const ReportProblemForm = ({ feature, source, errorContext, onClose }) => {
    const [whatDoing, setWhatDoing] = useState('');
    const [whatHappened, setWhatHappened] = useState('');
    const [whatExpected, setWhatExpected] = useState('');
    const [email, setEmail] = useState('');
    const [attachment, setAttachment] = useState(null); // { name, mime, data }
    const [status, setStatus] = useState('idle'); // idle | sending | success | error
    const [error, setError] = useState(null);
    const [ticketRef, setTicketRef] = useState(null);
    const [diagnostics, setDiagnostics] = useState(null);
    const overlayRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        getReportDiagnostics().then((d) => { if (!cancelled) setDiagnostics(d); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current && status !== 'sending') onClose();
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) { setAttachment(null); return; }
        if (file.size > MAX_ATTACHMENT_BYTES) {
            setError('Attachment is too large — the limit is 5MB.');
            e.target.value = '';
            return;
        }
        setError(null);
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = String(reader.result).split(',')[1] || '';
            setAttachment({ name: file.name, mime: file.type || 'application/octet-stream', data: base64 });
        };
        reader.onerror = () => setError('Could not read the attached file.');
        reader.readAsDataURL(file);
    };

    const clearAttachment = () => {
        setAttachment(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!whatHappened.trim()) {
            setError('Please describe what happened.');
            return;
        }
        if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            setError('That email address doesn’t look right.');
            return;
        }
        setError(null);
        setStatus('sending');
        const result = await submitProblemReport({
            whatDoing: whatDoing.trim() || null,
            whatHappened: whatHappened.trim(),
            whatExpected: whatExpected.trim() || null,
            email: email.trim() || null,
            feature: feature || null,
            errorContext: errorContext || null,
            attachment,
        });
        if (result?.ok) {
            setStatus('success');
            setTicketRef(result.ref || null);
            try {
                window.electron?.analytics?.track('problem_report_submitted', {
                    source: source || 'unknown',
                    feature: feature || null,
                    has_attachment: !!attachment,
                    has_email: !!email.trim(),
                });
            } catch { /* analytics is best-effort */ }
        } else {
            setStatus('error');
            setError(result?.error || 'Could not send the report. Please try again.');
        }
    };

    return (
        <div className="report-problem-overlay" ref={overlayRef} onMouseDown={handleOverlayClick}>
            <div className="report-problem-modal" role="dialog" aria-modal="true" aria-labelledby="report-problem-title">
                <div className="report-problem-header">
                    <h2 id="report-problem-title">Report a problem</h2>
                    <button className="report-problem-close" onClick={onClose} aria-label="Close" disabled={status === 'sending'}>×</button>
                </div>

                {status === 'success' ? (
                    <div className="report-problem-success">
                        <div className="report-problem-success-icon">✓</div>
                        <h3>Thanks — your report was sent</h3>
                        <p>
                            {email.trim()
                                ? 'We’ll follow up by email if we need more details.'
                                : 'It was sent anonymously — add an email next time if you’d like a reply.'}
                        </p>
                        {ticketRef && (
                            <p className="report-problem-ticket">
                                Your ticket number is <strong>{ticketRef}</strong> — mention it if you contact support.
                            </p>
                        )}
                        <button className="report-problem-submit" onClick={onClose}>Close</button>
                    </div>
                ) : (
                    <form className="report-problem-form" onSubmit={handleSubmit} noValidate>
                        <label className="report-problem-field">
                            <span>What were you trying to do?</span>
                            <textarea
                                rows={2}
                                value={whatDoing}
                                onChange={(e) => setWhatDoing(e.target.value)}
                                placeholder="e.g. Export cue points to Rekordbox"
                                disabled={status === 'sending'}
                                autoFocus
                            />
                        </label>
                        <label className="report-problem-field">
                            <span>What happened? <em className="report-problem-required">required</em></span>
                            <textarea
                                rows={3}
                                value={whatHappened}
                                onChange={(e) => { setWhatHappened(e.target.value); if (error) setError(null); }}
                                placeholder="e.g. The export finished but no cues showed up in Rekordbox"
                                disabled={status === 'sending'}
                            />
                        </label>
                        <label className="report-problem-field">
                            <span>What did you expect?</span>
                            <textarea
                                rows={2}
                                value={whatExpected}
                                onChange={(e) => setWhatExpected(e.target.value)}
                                placeholder="e.g. Hot cues on every exported track"
                                disabled={status === 'sending'}
                            />
                        </label>

                        <div className="report-problem-row">
                            <label className="report-problem-field">
                                <span>Email (optional — for a reply)</span>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
                                    placeholder="you@example.com"
                                    disabled={status === 'sending'}
                                    autoComplete="email"
                                />
                            </label>
                        </div>

                        <div className="report-problem-attachment">
                            <label className="report-problem-attach-btn">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={handleFileChange}
                                    disabled={status === 'sending'}
                                />
                                {attachment ? 'Replace attachment' : 'Attach a screenshot or file'}
                            </label>
                            {attachment && (
                                <span className="report-problem-attach-name">
                                    {attachment.name}
                                    <button type="button" onClick={clearAttachment} aria-label="Remove attachment" disabled={status === 'sending'}>×</button>
                                </span>
                            )}
                        </div>

                        <details className="report-problem-diagnostics">
                            <summary>What’s included automatically</summary>
                            <ul>
                                <li>MixGoat version: {diagnostics?.appVersion || '…'}</li>
                                <li>System: {diagnostics?.osVersion || '…'}{diagnostics?.arch ? ` · ${diagnostics.arch}` : ''}</li>
                                <li>Rekordbox: {diagnostics ? (diagnostics.rekordboxVersion || 'not detected') : '…'}</li>
                                <li>Feature in use: {feature || 'none'}</li>
                                <li>Recent app logs (no personal files)</li>
                                <li>Anonymous installation ID{diagnostics?.installId ? `: ${diagnostics.installId.slice(0, 8)}…` : ''}</li>
                                {errorContext ? <li>Details of the error that just occurred</li> : null}
                            </ul>
                        </details>

                        {error && <div className="report-problem-error">{error}</div>}

                        <div className="report-problem-actions">
                            <button
                                type="button"
                                className="report-problem-cancel"
                                onClick={onClose}
                                disabled={status === 'sending'}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="report-problem-submit"
                                disabled={status === 'sending' || !whatHappened.trim()}
                            >
                                {status === 'sending' ? 'Sending…' : 'Send report'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ReportProblemForm;
